import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import _ from 'lodash';
import {css, cx} from 'emotion';

import ProcessField from 'components/core/fields/ProcessField';
import InfoMessages from 'components/core/help/InfoMessages';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIEToggle from 'components/core/editable/RIEToggle';
import RIERadioGroup from 'components/core/editable/RIERadioGroup';
import ProcessService from 'services/ProcessService';
import { colorLabelBlue } from 'styles/base';

const wrapperStyle = css`
    min-width: 650px;
    .editable-container {
        min-width: 650px;
    }
`;

export default class ProcessOutputSettingsField extends ProcessField {

    static propTypes = Object.assign({
        runParameters: PropTypes.object,      // pass if running in runParameters mode
        labelWidth: PropTypes.string
    }, ProcessField.propTypes);

    static defaultProps = Object.assign({
    }, ProcessField.defaultProps);

    constructor(props) {
        super(props);
        this.onIncludeTransformationMetricsUpdated = this.onParameterUpdated.bind(this, 'include_transformation_metrics');
        this.onIncludeGlattrVarsUpdated = this.onParameterUpdated.bind(this, 'include_glattr_vars');
        this.onIncludeDQRAssessmentsUpdated = this.onParameterUpdated.bind(this, 'include_dqr_assessments');
        this.onRollupQCUpdated = this.onParameterUpdated.bind(this, 'rollup_qc');
        this.onMissingValuesDispositionUpdated = this.onParameterUpdated.bind(this, 'missing_values_disposition');
    }

    onParameterUpdated = (key, fieldPath, value) => {
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters[key] = value;
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    onIncludeQCVarsUpdated = (fieldPath, value) => {
        const parameters = _.cloneDeep(this.props.runParameters);
        parameters['include_qc_vars'] = value;

        // When the include qc vars setting changes, flip the corresonding
        // params so they appear in a consistent state;
        parameters['rollup_qc'] = value;

        ProcessService.updateRunParameters(this.props.process.name, parameters);
    };

    render() {
        const options = {
            'NO_CHANGE': 'Leave them unchanged',
            'SET_BAD_MISSING': 'Set bad values to missing_value',
            'SET_INDETERMINATE_MISSING': 'Set bad and indeterminate values to missing_value'
        } ;

        const qcHeader = css`
            width: 25px;
            padding: 20px 5px;
            margin-right: 15px;
            background-color: ${colorLabelBlue};
            color: white;
            justify-content: center;
        `;

        const labelWidth = this.props.labelWidth ? this.props.labelWidth : null;

        return (
            <Flexbox flexDirection="row" alignItems="flex-start" className={cx(wrapperStyle, this.props.className)}>
                <InfoBoxLabel label="Output Settings" width={labelWidth} infoIcon={false} alignTop />
                <Flexbox flexDirection="column">
                    <Flexbox flexDirection="row" alignItems="flex-start" marginBottom="20px">
                        <Flexbox flexDirection="row" alignItems="flex-start"  marginRight="50px">
                            <InfoBoxLabel label="Include transformation metrics?" width="161px" hideColon
                                          message={InfoMessages.datasetDumps} alignTop labelWrap/>
                            <RIEToggle
                                value={this.props.runParameters.include_transformation_metrics}
                                change={this.onIncludeTransformationMetricsUpdated}
                                textFalse="No"
                                textTrue="Yes"
                                radioGroupName="include-transformation-metrics"
                                formDisplay
                                isDisabled={false}
                                minWidth="150px"
                            />
                        </Flexbox>
                        <Flexbox flexDirection="row" alignItems="flex-start">
                            <InfoBoxLabel label="Include input datastream global attributes?" labelWrap hideColon
                                          width="176px" message={InfoMessages.datasetDumps} alignTop/>
                            <RIEToggle
                                value={this.props.runParameters.include_glattr_vars}
                                change={this.onIncludeGlattrVarsUpdated}
                                textFalse="No"
                                textTrue="Yes"
                                radioGroupName="include-glattr-vars"
                                formDisplay
                                isDisabled={false}
                                minWidth="150px"
                            />
                        </Flexbox>
                    </Flexbox>
                    <Flexbox flexDirection="row" alignItems="stretch">

                        <Flexbox flexDirection="column" alignItems="center" className={qcHeader}>QC</Flexbox>

                        <Flexbox flexDirection="column" marginRight="49px">
                            <Flexbox flexDirection="row" alignItems="flex-start" marginBottom="15px">
                                <InfoBoxLabel label="Include QC variables?" width="121px" hideColon
                                              message={InfoMessages.datasetDumps} alignTop labelWrap/>
                                <RIEToggle
                                    value={this.props.runParameters.include_qc_vars}
                                    change={this.onIncludeQCVarsUpdated}
                                    textFalse="No"
                                    textTrue="Yes"
                                    radioGroupName="include-qc-vars"
                                    formDisplay
                                    isDisabled={false}
                                    minWidth="150px"
                                />
                            </Flexbox>
                            <Flexbox flexDirection="row" alignItems="flex-start" marginBottom="15px">
                                <InfoBoxLabel label="Add DQR assessments?" width="121px" hideColon
                                              message={InfoMessages.datasetDumps} alignTop labelWrap/>
                                <RIEToggle
                                    value={this.props.runParameters.include_dqr_assessments}
                                    change={this.onIncludeDQRAssessmentsUpdated}
                                    textFalse="No"
                                    textTrue="Yes"
                                    radioGroupName="include-dqr-assessments"
                                    formDisplay
                                    isDisabled={false}
                                    minWidth="150px"
                                />
                            </Flexbox>
                            {this.props.runParameters.include_qc_vars === true &&
                                <Flexbox flexDirection="row" alignItems="flex-start" marginBottom="15px">
                                    <InfoBoxLabel label="Roll-up QC?" width="121px" hideColon
                                                  message={InfoMessages.datasetDumps} alignTop labelWrap/>
                                    <RIEToggle
                                        value={this.props.runParameters.rollup_qc}
                                        change={this.onRollupQCUpdated}
                                        textFalse="No"
                                        textTrue="Yes"
                                        radioGroupName="rollup-qc"
                                        formDisplay
                                        isDisabled={false}
                                        minWidth="150px"
                                    />
                                </Flexbox>
                            }
                        </Flexbox>
                        {this.props.runParameters.include_qc_vars === false &&
                            <Flexbox flexDirection="column">
                                <InfoBoxLabel label="How should we handle bad/indeterminate values?" labelWrap
                                              width="350px" message={InfoMessages.datasetDumps} alignTop hideColon/>
                                <RIERadioGroup
                                    value={this.props.runParameters.missing_values_disposition}
                                    options={options}
                                    change={this.onMissingValuesDispositionUpdated}
                                    formDisplay
                                    isDisabled={false}
                                    minWidth="245px"
                                    radioGroupName="missing-value-disposition"
                                />
                            </Flexbox>
                        }
                    </Flexbox>
                </Flexbox>
            </Flexbox>

        );
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/ProcessOutputSettingsField.js